/** Numerical results with error estimate + closed-form reference row. */
import { siFormat } from "../lib/format";
import { cf, design, hasErrors, solveState, updateDesign, engineReady } from "../state/store";
import { solver } from "../solver/client";
import { Select } from "./common";

function Row(props: { label: string; value: string; title?: string; muted?: boolean }) {
  return (
    <div class={`result-row ${props.muted ? "muted" : ""}`} title={props.title}>
      <span>{props.label}</span>
      <span class="result-value">{props.value}</span>
    </div>
  );
}

export function ResultsPanel() {
  const st = solveState.value;
  const c = cf.value;
  const r = st.result;
  const solving = st.status === "solving";
  const num = r && !hasErrors.value ? r : undefined;
  const deltaPpm =
    num && c.available && c.z0 ? ((num.z0 - c.z0) / c.z0) * 1e6 : null;

  return (
    <div class="results">
      {!engineReady.value && <div class="status-line">Loading solver…</div>}
      {hasErrors.value && <div class="status-line error">Fix the highlighted inputs to solve.</div>}
      {st.status === "error" && <div class="status-line error">Solve failed: {st.error}</div>}

      <div class={`result-block ${solving ? "solving" : ""}`}>
        <div class="result-head">
          <span>Numerical{num ? ` · ${num.dof.toLocaleString()} DOF` : ""}</span>
          {solving && (
            <span class="spinner-wrap">
              <span class="spinner" />
              <button class="mini" title="Restart the solver" onClick={() => solver.restart()}>
                ✕
              </button>
            </span>
          )}
        </div>
        <Row label="Z₀" value={num ? `${num.z0.toFixed(4)} Ω` : "—"} />
        <Row label="C′" value={num ? siFormat(num.capacitance, "F/m") : "—"} />
        <Row label="εeff" value={num ? num.epsEff.toFixed(5) : "—"} />
        {num && num.errorEstimate > 0 && (
          <Row label="est. error" value={`±${(num.errorEstimate * 100).toFixed(3)} %`} muted />
        )}
        {num && <Row label="solve time" value={`${num.elapsedMs.toFixed(0)} ms`} muted />}
      </div>

      <div class="result-block reference">
        <div class="result-head">
          <span>Closed form</span>
        </div>
        {c.available ? (
          <>
            <Row label="Z₀" value={`${c.z0!.toFixed(4)} Ω`} />
            <Row label="εeff" value={c.epsEff!.toFixed(5)} />
            {deltaPpm !== null && (
              <Row label="Δ numerical" value={`${deltaPpm >= 0 ? "+" : ""}${deltaPpm.toFixed(0)} ppm`} muted />
            )}
          </>
        ) : (
          <div class="status-line info">{c.reason}</div>
        )}
      </div>

      <Select
        label="Accuracy"
        value={design.value.accuracy}
        options={[
          { value: "draft", label: "Draft (one-shot)" },
          { value: "normal", label: "Normal (~0.1–1 %)" },
          { value: "high", label: "High (adaptive, slow)" },
        ]}
        onChange={(accuracy) => updateDesign((d) => ({ ...d, accuracy }))}
      />
    </div>
  );
}
